import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Link, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import CustomCard from '../../../components/Antd/Card';
import CustomButton from '../../../v2-components/Antd/Button';
import CustomDivider from '../../../v2-components/Antd/Divider';
import CustomDragSortTable from '../../../v2-components/Antd/DragTable';
import CustomModal from '../../../v2-components/Antd/Modal';
import CustomSwitch from '../../../v2-components/Antd/Switch';
import LoadingModal from '../../../v2-components/LoadingModal';
import { CustomNotification } from '../../../v2-components/Notification';
import { capitalizeFirstLetter } from '../handle/capitalize-first-letter.handle';
import { Service } from '../service';
import usePharmacyStore from '../templates/store/pharmacy-store';
import { Locations } from './constants/locations.constant';
import { nameAdapter } from '../locations/handle/name-adapter.handle';
import './style.scss';

export default function PharmacySliderResultsPage() {
	const { template, location } = useParams();
	const history = useHistory();
	const [openLoadingModal, setOpenLoadingModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [fileToDelete, setFileToDelete] = useState(null);
	const [loading, setLoading] = useState(true);
	const [pharmacyLocations, selectedPharmacy, setIsNewFile, platform, ecosystem, token] = usePharmacyStore((state) => [
		state.pharmacyLocations,
		state.selectedPharmacy,
		state.setIsNewFile,
		state.platform,
		state.ecosystem,
		state.token,
	]);
	const [dataSource, setDataSource] = useState([]);

	useEffect(() => {
		setLoading(true);
		if (pharmacyLocations.length > 0) {
			new Service()
				.getFilesByLocation(template, location, selectedPharmacy, ecosystem)
				.then((filesResponse) => {
					const data = filesResponse;
					const dataWithAddedKeys = data.map((module) => {
						const newModule = { ...module };
						newModule.key = module.position;
						newModule.pharmacy = selectedPharmacy;
						newModule.template = template;
						newModule.location = location;
						return newModule;
					});
					setDataSource(dataWithAddedKeys);
				})
				.catch((error) => {
					return CustomNotification({ type: 'error', description: 'Ha ocurrido un error al obtener los recursos:', message: error });
				});
		}
		setLoading(false);
	}, [location, pharmacyLocations, template, selectedPharmacy, ecosystem]);

	const handleOpenDeleteModal = (index) => {
		setOpenDeleteModal(true);
		const fileToDelete = dataSource.find((_, i) => i === index);
		setFileToDelete(fileToDelete);
	};
	const confirmDelete = () => {
		handleDelete(fileToDelete);
		setOpenDeleteModal(false);
	};

	const handleDelete = async (fileToDelete) => {
		setOpenLoadingModal(true);
		await new Service()
			.deleteFile(token, {
				template,
				location,
				id: fileToDelete?._id,
			})
			.then((response) => {
				return CustomNotification({ type: 'success', description: 'Archivo borrado exitosamente' });
			})
			.catch((error) => {
				return CustomNotification({ type: 'error', description: 'Ha ocurrido un error al borrar el archivo:', message: error });
			});

		const updatedDataSource = dataSource.filter((item) => item._id !== fileToDelete?._id);

		updatedDataSource.forEach((file, i) => {
			file.position = i + 1;
		});

		const positionDataSource = updatedDataSource.map((file) => {
			return { position: file.position, id: file._id };
		});

		new Service()
			.updateFilePosition(token, {
				template,
				location,
				files: positionDataSource,
			})
			.then((response) => {
				return CustomNotification({ type: 'success', description: 'Lista actualizada' });
			})
			.catch((error) => {
				return CustomNotification({
					type: 'error',
					description: 'Ha ocurrido un error al actualizar las posiciones de los archivo:',
					message: error,
				});
			});

		setDataSource(updatedDataSource);
		setOpenLoadingModal(false);
	};

	const handleSwitchChange = (index, checked) => {
		setOpenLoadingModal(true);
		const updatedDataSource = [...dataSource];
		const fileToUpdate = updatedDataSource[index];

		new Service()
			.updateFileStatus(token, {
				active: checked,
				template: template,
				location: location,
				id: fileToUpdate._id,
			})
			.then((response) => {
				return CustomNotification({ type: 'success', description: 'Estado cambiado correctamente' });
			})
			.catch((error) => {
				return CustomNotification({
					type: 'error',
					description: 'Ha ocurrido un error al mutar el estado active del archivo:',
					message: error,
				});
			});

		updatedDataSource[index].active = checked;

		setDataSource(updatedDataSource);
		setOpenLoadingModal(false);
	};

	const handleDragSortEnd = (beforeIndex, afterIndex, newDataSource) => {
		newDataSource.forEach((file, index) => {
			file.position = index + 1;
		});

		const positionDataSource = newDataSource.map((file) => {
			return { position: file.position, id: file._id };
		});

		new Service()
			.updateFilePosition(token, {
				template,
				location,
				files: positionDataSource,
			})
			.then((response) => {})
			.catch((error) => {
				return CustomNotification({
					type: 'error',
					description: 'Ha ocurrido un error al actualizar las posiciones de los archivo:',
					message: error,
				});
			});

		setDataSource(newDataSource);
	};

	const handleCreate = () => {
		if (dataSource.length >= 8) {
			return CustomNotification({ type: 'error', description: 'Tienes un límite de 8 imágenes para éste tipo de módulo' });
		} else {
			setIsNewFile({
				isNewFile: true,
				id: null,
			});
			history.push(`/v2/pharmacy-sliders/templates/${template}/locations/${location}/create`);
		}
	};

	const columns = [
		{
			title: 'Orden',
			dataIndex: 'order',
		},
		{
			title: 'Plataforma',
			dataIndex: 'pharmacy',
			render: (record) => `${capitalizeFirstLetter(nameAdapter(selectedPharmacy, platform))}`,
		},
		{
			title: 'Página',
			dataIndex: 'template',
		},
		{
			title: 'Módulo',
			dataIndex: 'location',
		},
		{
			title: 'Título de imagen (no visible en la web)',
			dataIndex: 'title',
		},
		{
			title: 'Estado',
			dataIndex: 'active',
			key: 'switch',
			align: 'center',
			width: 80,
			render: (_, row, index) => (
				<CustomSwitch checked={row.active} onChange={(checked) => handleSwitchChange(index, checked)} className="custom-switch" />
			),
		},
		{
			title: 'Acciones',
			dataIndex: 'acciones',
			key: 'delete',
			align: 'center',
			width: 80,
			render: (_, __, index) => <DeleteOutlined style={{ fontSize: '24px', cursor: 'pointer' }} onClick={() => handleOpenDeleteModal(index)} />,
		},
		{
			title: 'Ver más',
			dataIndex: 'ver mas',
			key: 'switch',
			align: 'center',
			width: 80,
			render: (_, row, index) => (
				<SearchOutlined
					style={{ fontSize: '24px' }}
					onClick={() => {
						setIsNewFile({
							isNewFile: false,
							id: index,
							file: row,
						});
						history.push(`/v2/pharmacy-sliders/templates/${template}/locations/${location}/create`);
					}}
				/>
			),
		},
	];

	return (
		<div className="pharmacy-sliders-results">
			<h1>
				{capitalizeFirstLetter(nameAdapter(selectedPharmacy, platform))} / {template} {selectedPharmacy === 'fanich' ? 'Anich' : selectedPharmacy} / {location}
			</h1>
			<Link to={`/v2/pharmacy-sliders/templates/${template}`}>
				Volver a {capitalizeFirstLetter(nameAdapter(selectedPharmacy, platform))} / {template} {selectedPharmacy === 'fanich' ? 'Anich' : selectedPharmacy}
			</Link>
			<Row className="results-container" gutter={[16, 16]}>
				<Col md={24} xs={24}>
					<CustomCard
						className="modules-card"
						title={
							<div className="card-header-container">
								<h3>Módulo {location}</h3>
								<div className="inner-container">
									<p>
										Para que la sección se muestre debes tener al menos 1 imagen activa.{' '}
										{(location === Locations['horizontal-box'] || location === Locations['vertical-box']) &&
											'Te recomendamos subir de a pares para que se vea mejor.'}
									</p>
									<CustomButton onClick={handleCreate}>Crear</CustomButton>
								</div>
							</div>
						}
					>
						<CustomDragSortTable
							loading={loading}
							headerTitle=""
							rowKey="key"
							search={false}
							pagination={false}
							columns={columns}
							dataSource={dataSource}
							dragSortKey="order"
							onDragSortEnd={handleDragSortEnd}
						/>
					</CustomCard>
				</Col>
			</Row>
			<LoadingModal visible={openLoadingModal} />
			<CustomModal
				title="Eliminar imagen"
				onCancel={() => setOpenDeleteModal(false)}
				footer={null}
				open={openDeleteModal}
				wrapClassName="pharmacy-results-delete-modal"
			>
				<h3>¿Estas seguro que deseas eliminar la siguiente imagen?</h3>
				<p>{fileToDelete?.title}</p>
				<CustomDivider />
				<div className="buttons-container">
					<CustomButton className={'cancel-buttom'} onClick={() => setOpenDeleteModal(false)}>
						Cancelar
					</CustomButton>
					<CustomButton className={'confirm-buttom'} onClick={confirmDelete}>
						Confirmar
					</CustomButton>
				</div>
			</CustomModal>
		</div>
	);
}
